import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spinner, LoadingButton, SimpleButton, TextArea, Input, Toast, NotificationsList, useStateContext } from '@veda/dashboard-ui';
import { BsArrowLeft, BsPaperclip, BsDownload } from 'react-icons/bs';
import { usePppApi } from '../../ppp/services/pppApi';
import { usePppAiContext } from '../../ppp/hooks/usePppAiContext';
import { useDownload } from '../../ppp/hooks/useDownload';

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('it-IT');
}

function formatBytes(b) {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex flex-col">
      <span className="text-xs text-gray-500 uppercase tracking-wide">{label}</span>
      <span className="text-sm text-gray-900 dark:text-white mt-0.5 whitespace-pre-wrap">{value || '—'}</span>
    </div>
  );
}

const TODAY = new Date().toISOString().slice(0, 10);

export default function OperatoreAvvisoDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  usePppAiContext('ppp.avviso', { id });
  const { _locate, addToast } = useStateContext();
  const api = usePppApi();
  const { download } = useDownload();

  const [avviso, setAvviso]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [interesse, setInteresse] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [showRichiesta, setShowRichiesta] = useState(false);
  const [richForm, setRichForm] = useState({ oggetto: '', testo: '' });
  const [richErrors, setRichErrors] = useState(null);

  const carica = useCallback(() => {
    setLoading(true);
    api.getAvvisoOperatore(id, {
      onSuccess: (data) => { setAvviso(data); setInteresse(!!data.interesseRegistrato); setLoading(false); },
      onError: () => setLoading(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => { carica(); }, [carica]);

  const handleInteresse = () => {
    setSaving(true);
    api.registraInteresseAvviso(id, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate('ppp_avviso_operatore_interesse_ok') ?? 'Interesse registrato con successo'} />);
        setSaving(false); setInteresse(true);
      },
      onError: () => setSaving(false),
    });
  };

  const handleRichiesta = () => {
    if (!richForm.oggetto || !richForm.testo) return;
    setSaving(true); setRichErrors(null);
    api.creaRichiestaDocAvviso(id, richForm, {
      onSuccess: () => {
        addToast(<Toast duration={4} content={_locate('ppp_avviso_richiesta_creata') ?? 'Richiesta inviata'} />);
        setSaving(false); setShowRichiesta(false);
        setRichForm({ oggetto: '', testo: '' });
      },
      onError: () => setSaving(false),
      onFormError: (response) => { setSaving(false); setRichErrors(response.errors); },
    });
  };

  if (loading) return <div className="flex items-center justify-center min-h-64"><Spinner /></div>;
  if (!avviso) return null;

  const allegati = avviso.allegati ?? [];

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/operatore/avvisi')}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-3"
          >
            <BsArrowLeft /> {_locate('ppp_avviso_titolo_lista') ?? 'Avvisi pubblici'}
          </button>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{avviso.oggetto}</h1>
              {avviso.procedimentoTitolo && (
                <p className="text-sm text-gray-500 dark:text-gray-400">{avviso.procedimentoTitolo}</p>
              )}
            </div>
            <div className="shrink-0">
              {interesse ? (
                <span className="text-sm px-3 py-1.5 rounded bg-green-100 text-green-700 font-medium">
                  {_locate('ppp_avviso_operatore_gia_registrato') ?? 'Interesse registrato'}
                </span>
              ) : (
                <LoadingButton
                  isLoading={saving}
                  customFunc={handleInteresse}
                  text={_locate('ppp_avviso_operatore_interesse') ?? 'Registra interesse'}
                />
              )}
            </div>
          </div>
        </div>

        {/* Date chiave */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
          <div className="grid grid-cols-2 gap-4">
            <InfoRow label={_locate('ppp_avviso_data_pubblicazione') ?? 'Pubblicato il'} value={formatDate(avviso.dataPubblicazione)} />
            <InfoRow label={_locate('ppp_avviso_termine_proposte') ?? 'Termine proposte'} value={formatDate(avviso.terminePresentazioneProposte)} />
          </div>
        </div>

        {/* Contenuto */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4 space-y-4">
          <InfoRow label={_locate('ppp_avviso_descrizione_intervento') ?? 'Descrizione intervento'} value={avviso.descrizioneIntervento} />
          <InfoRow label={_locate('ppp_avviso_requisiti_minimi') ?? 'Requisiti minimi'} value={avviso.requisitiMinimi} />
        </div>

        {/* Allegati */}
        {allegati.length > 0 && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5 mb-4">
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
              {_locate('ppp_avviso_allegati') ?? 'Allegati'}
            </h2>
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {allegati.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <BsPaperclip className="text-gray-400 shrink-0" />
                    <span className="text-sm text-gray-900 dark:text-white font-medium">{a.nome}</span>
                    <span className="text-xs text-gray-400">{a.fileNome}</span>
                    {a.fileDimensione && (
                      <span className="text-xs text-gray-400">({formatBytes(a.fileDimensione)})</span>
                    )}
                  </div>
                  <button
                    onClick={() => download(`/api/v1/operatore/avvisi/${id}/allegati/${a.id}/download`, a.fileNome)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-3 py-1.5 border border-blue-200 rounded hover:bg-blue-50 shrink-0 ml-4"
                  >
                    <BsDownload size={13} /> Scarica
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Risposte della PA alle richieste di integrazione */}
        {avviso.risposteIntegrazioni && avviso.risposteIntegrazioni.length > 0 && (
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-5 mb-4">
            <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-3">
              {_locate('ppp_avviso_risposte_integrazioni') ?? 'Risposte della PA'}
            </h2>
            <div className="space-y-4">
              {avviso.risposteIntegrazioni.map((r) => (
                <div key={r.id} className="bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 rounded p-3">
                  <p className="text-xs font-semibold text-green-700 dark:text-green-400 mb-1">{r.oggetto}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{r.testoRisposta}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-gray-400">{formatDate(r.dataRisposta)}</span>
                    {r.fileRispostaUrl && (
                      <button
                        onClick={() => download(r.fileRispostaUrl, r.fileRisposta ?? 'allegato')}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-200 rounded hover:bg-blue-50"
                      >
                        <BsDownload size={12} /> {r.fileRisposta ?? 'Scarica allegato'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Richiedi documenti integrativi */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-5">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                {_locate('ppp_avviso_richiedi_documenti') ?? 'Richiesta documenti integrativi'}
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                {_locate('ppp_avviso_richiedi_documenti_hint') ?? 'Puoi richiedere chiarimenti o documentazione integrativa alla PA.'}
              </p>
            </div>
            {!showRichiesta && (
              <button
                onClick={() => setShowRichiesta(true)}
                className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700"
              >
                {_locate('ppp_avviso_aggiungi_richiesta') ?? 'Nuova richiesta'}
              </button>
            )}
          </div>

          {showRichiesta && (
            <div className="space-y-3 mt-3">
              <Input
                label={_locate('ppp_avviso_richiesta_oggetto') ?? 'Oggetto della richiesta'}
                required
                value={richForm.oggetto}
                onChange={(e) => setRichForm((f) => ({ ...f, oggetto: e.target.value }))}
              />
              <TextArea
                label={_locate('ppp_avviso_richiesta_testo') ?? 'Testo della richiesta'}
                rows={4} required
                value={richForm.testo}
                onChange={(e) => setRichForm((f) => ({ ...f, testo: e.target.value }))}
              />
              <NotificationsList errors={richErrors} />
              <div className="flex gap-2 justify-end">
                <SimpleButton customFunc={() => setShowRichiesta(false)} text={_locate('ppp_annulla') ?? 'Annulla'} />
                <LoadingButton
                  isLoading={saving}
                  customFunc={richForm.oggetto && richForm.testo ? handleRichiesta : undefined}
                  text={_locate('ppp_avviso_salva') ?? 'Invia richiesta'}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
